import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  updateContactGroup,
  updateContactGroupUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ApiException, ContactGroup } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import axios from 'axios';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

interface EditContactGroupProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userView?: boolean;
  contactGroupList: ContactGroup[] | undefined;
  contactGroup: ContactGroup;
  changeLogRoles: string[];
}

export default function EditContactGroupDialog({
  open,
  handleClose,
  userEmail,
  userView,
  contactGroup,
  contactGroupList,
  changeLogRoles,
}: EditContactGroupProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [groupName, setGroupName] = useState(contactGroup.formattedName);
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  useEffect(() => {
    setGroupName(contactGroup.formattedName);
    if (hasError && !open) {
      setHasError(false);
    }
  }, [contactGroup.formattedName, open, hasError]);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      updateContactGroup(userEmail, contactGroup, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Label updated successfully!',
      });

      const newList = contactGroupList?.filter(
        (group) => group.resourceName != contactGroup.resourceName
      );
      newList?.push(data);
      queryClient.setQueryData([UserKeys.Labels, userEmail], newList);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message?.includes('409')) {
          setHasError(true);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              'There was an issue updating this label. Please refresh and try again.',
          });
        }
      }
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .update_contact_label,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.personalContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () => updateContactGroupUserPortal(contactGroup),
      onSuccess: (data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Label updated successfully!',
        });

        const newList = contactGroupList?.filter(
          (group) => group.resourceName != contactGroup.resourceName
        );
        newList?.push(data);
        queryClient.setQueryData([UserKeys.Labels, userEmail], newList);
        setChangeLogMessage('');
        handleClose();
      },
      onError: (error) => {
        if (axios.isAxiosError(error)) {
          const errorResponse = error.response?.data as ApiException;
          if (errorResponse.message?.includes('409')) {
            setHasError(true);
          } else {
            handleErrors({
              error,
              snackbarMessage:
                'There was an issue updating this label. Please refresh and try again.',
            });
          }
        }
      },
    });

  function handleSubmit() {
    contactGroup.name = groupName;
    if (
      !userView &&
      isAbleToSeeUIElement(changeLogRoles, UserRoles.EditContactGroups.value)
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      userPortalMutate();
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Edit Label"
      dialogContent={
        <Box>
          <TextField
            autoFocus
            id={contactGroup.resourceName || ''}
            label="Label Name"
            error={hasError}
            helperText={hasError ? 'Label names taken.' : ''}
            fullWidth
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: groupName.length === 0,
      }}
    />
  );
}
